import React, { createContext, useContext, useState, useEffect } from 'react';
import { Friend, FriendRequest } from '../types/social';
import { useAuthState } from '../hooks/useAuthState';
import { supabase, handleSupabaseError } from '../config/supabase';
import toast from 'react-hot-toast';

interface SocialContextType {
  friends: Friend[];
  friendRequests: FriendRequest[];
  loading: boolean;
  addFriend: (friendId: string) => Promise<void>;
  removeFriend: (friendId: string) => Promise<void>;
  sendFriendRequest: (receiverId: string) => Promise<void>;
  acceptFriendRequest: (requestId: string) => Promise<void>;
  declineFriendRequest: (requestId: string) => Promise<void>;
  getUserById: (userId: string) => Promise<any>;
  getAllUsers: () => Promise<any[]>;
}

const SocialContext = createContext<SocialContextType | undefined>(undefined);

export const SocialProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [friends, setFriends] = useState<Friend[]>([]);
  const [friendRequests, setFriendRequests] = useState<FriendRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuthState();

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const loadSocialData = async () => {
      try {
        setLoading(true);
        
        // Load friends
        const { data: friendsData, error: friendsError } = await supabase
          .from('friends')
          .select(`
            id,
            user1_id,
            user2_id,
            status
          `)
          .or(`user1_id.eq.${user.id},user2_id.eq.${user.id}`)
          .eq('status', 'accepted');

        if (friendsError) throw friendsError;

        // Load friend requests
        const { data: requestsData, error: requestsError } = await supabase
          .from('friend_requests')
          .select(`
            id,
            sender_id,
            receiver_id,
            status,
            created_at
          `)
          .eq('receiver_id', user.id)
          .eq('status', 'pending');

        if (requestsError) throw requestsError;

        // Transform friends data - get friend user IDs first
        const friendUserIds = friendsData?.map(friend => 
          friend.user1_id === user.id ? friend.user2_id : friend.user1_id
        ) || [];

        // Fetch user data for friends
        const { data: friendUsersData, error: friendUsersError } = await supabase
          .from('users')
          .select('id, name, email, avatar_url')
          .in('id', friendUserIds);

        if (friendUsersError) throw friendUsersError;

        const transformedFriends = friendsData?.map(friend => {
          const friendUserId = friend.user1_id === user.id ? friend.user2_id : friend.user1_id;
          const friendUser = friendUsersData?.find(u => u.id === friendUserId);
          
          return {
            id: friendUserId,
            name: friendUser?.name || 'Unknown User',
            email: friendUser?.email || '',
            avatarUrl: friendUser?.avatar_url || '',
            status: 'offline', // You might want to implement online status
            focusTime: 0, // You might want to get this from user_stats
            level: 1, // You might want to get this from user_stats
            experience: 0, // You might want to get this from user_stats
            achievements: 0, // You might want to get this from achievements
            currentStreak: 0, // You might want to get this from user_stats
          };
        }) || [];

        // Transform friend requests data - get sender user IDs first
        const senderUserIds = requestsData?.map(request => request.sender_id) || [];

        // Fetch user data for senders
        const { data: senderUsersData, error: senderUsersError } = await supabase
          .from('users')
          .select('id, name, email, avatar_url')
          .in('id', senderUserIds);

        if (senderUsersError) throw senderUsersError;

        const transformedRequests = requestsData?.map(request => {
          const senderUser = senderUsersData?.find(u => u.id === request.sender_id);
          
          return {
            id: request.id,
            senderId: request.sender_id,
            senderName: senderUser?.name || 'Unknown User',
            senderAvatar: senderUser?.avatar_url || '',
            receiverId: request.receiver_id,
            status: request.status,
            createdAt: new Date(request.created_at).getTime(),
          };
        }) || [];

        setFriends(transformedFriends);
        setFriendRequests(transformedRequests);
      } catch (error) {
        console.error('Error loading social data:', error);
        if (error.message?.includes('Failed to fetch') || error.message?.includes('ERR_NAME_NOT_RESOLVED')) {
          console.warn('Network error detected, using empty social data');
          setFriends([]);
          setFriendRequests([]);
        } else {
          handleSupabaseError(error);
        }
      } finally {
        setLoading(false);
      }
    };

    // Social features disabled — community is off
    // loadSocialData();
  }, [user]);

  const addFriend = async (friendId: string) => {
    if (!user) throw new Error('User not authenticated');

    try {
      const { error } = await supabase
        .from('friends')
        .insert({
          user1_id: user.id,
          user2_id: friendId,
          status: 'accepted',
        });

      if (error) throw error;
      toast.success('Friend added successfully');
    } catch (error) {
      console.error('Error adding friend:', error);
      handleSupabaseError(error);
      throw error;
    }
  };

  const removeFriend = async (friendId: string) => {
    if (!user) throw new Error('User not authenticated');

    try {
      const { error } = await supabase
        .from('friends')
        .delete()
        .or(`and(user1_id.eq.${user.id},user2_id.eq.${friendId}),and(user1_id.eq.${friendId},user2_id.eq.${user.id})`);

      if (error) throw error;
      setFriends(prev => prev.filter(f => f.id !== friendId));
      toast.success('Friend removed successfully');
    } catch (error) {
      console.error('Error removing friend:', error);
      handleSupabaseError(error);
      throw error;
    }
  };

  const sendFriendRequest = async (receiverId: string) => {
    if (!user) throw new Error('User not authenticated');

    try {
      const { error } = await supabase
        .from('friend_requests')
        .insert({
          sender_id: user.id,
          receiver_id: receiverId,
          status: 'pending',
        });

      if (error) throw error;
      toast.success('Friend request sent');
    } catch (error) {
      console.error('Error sending friend request:', error);
      handleSupabaseError(error);
      throw error;
    }
  };

  const acceptFriendRequest = async (requestId: string) => {
    if (!user) throw new Error('User not authenticated');

    try {
      // Update the friend request status
      const { error: requestError } = await supabase
        .from('friend_requests')
        .update({ status: 'accepted' })
        .eq('id', requestId);

      if (requestError) throw requestError;

      // Get the sender ID from the request
      const { data: requestData, error: fetchError } = await supabase
        .from('friend_requests')
        .select('sender_id')
        .eq('id', requestId)
        .single();

      if (fetchError) throw fetchError;

      // Create the friendship
      const { error: friendError } = await supabase
        .from('friends')
        .insert({
          user1_id: user.id,
          user2_id: requestData.sender_id,
          status: 'accepted',
        });

      if (friendError) throw friendError;

      // Remove the request from the list
      setFriendRequests(prev => prev.filter(r => r.id !== requestId));
      toast.success('Friend request accepted');
    } catch (error) {
      console.error('Error accepting friend request:', error);
      handleSupabaseError(error);
      throw error;
    }
  };

  const declineFriendRequest = async (requestId: string) => {
    try {
      const { error } = await supabase
        .from('friend_requests')
        .update({ status: 'declined' })
        .eq('id', requestId);

      if (error) throw error;

      setFriendRequests(prev => prev.filter(r => r.id !== requestId));
      toast.success('Friend request declined');
    } catch (error) {
      console.error('Error declining friend request:', error);
      handleSupabaseError(error);
      throw error;
    }
  };

  const getUserById = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching user:', error);
      handleSupabaseError(error);
      throw error;
    }
  };

  const getAllUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .neq('id', user?.id); // Exclude current user

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching users:', error);
      handleSupabaseError(error);
      throw error;
    }
  };

  return (
    <SocialContext.Provider value={{
      friends,
      friendRequests,
      loading,
      addFriend,
      removeFriend,
      sendFriendRequest,
      acceptFriendRequest,
      declineFriendRequest,
      getUserById,
      getAllUsers,
    }}>
      {children}
    </SocialContext.Provider>
  );
};

export const useSocial = () => {
  const context = useContext(SocialContext);
  if (context === undefined) {
    throw new Error('useSocial must be used within a SocialProvider');
  }
  return context;
};